export const TEST_FEATURE_UNAVAILABLE_CODE = "TEST_FEATURE_UNAVAILABLE";
export const TEST_FEATURE_UNAVAILABLE_EVENT = "wb:test-feature-unavailable";

const TEST_HOST_PATTERN = /test\.workbrook/i;

/**
 * True when external AI / job-match services must be disabled (test builds).
 * Set REACT_APP_DISABLE_EXTERNAL_AI=true in test .env, or use test.workbrook URLs.
 */
export function isTestEnvironment() {
  if (process.env.REACT_APP_DISABLE_EXTERNAL_AI === "true") {
    return true;
  }

  const baseUrl = process.env.REACT_APP_BASE_URL || "";
  if (TEST_HOST_PATTERN.test(baseUrl)) {
    return true;
  }

  const webUrl = process.env.REACT_APP_WEB_URL || "";
  if (TEST_HOST_PATTERN.test(webUrl)) {
    return true;
  }

  if (typeof window !== "undefined" && TEST_HOST_PATTERN.test(window.location.hostname)) {
    return true;
  }

  return false;
}

export function createTestFeatureUnavailableError(
  featureName = "This feature",
) {
  const error = new Error(
    `${featureName} is not available on the test environment.`,
  );
  error.code = TEST_FEATURE_UNAVAILABLE_CODE;
  error.isTestFeatureUnavailable = true;
  return error;
}

export function isTestFeatureUnavailableError(error) {
  return (
    error?.code === TEST_FEATURE_UNAVAILABLE_CODE ||
    error?.isTestFeatureUnavailable === true
  );
}

/** True when a service returned the test-environment block object (no alert/toast needed). */
export function isTestUnavailableResult(result) {
  return result?.testUnavailable === true;
}

/** Skip default alert/toast — the global modal is already shown. */
export function shouldSuppressTestUnavailableFeedback(errorOrResult) {
  return (
    isTestUnavailableResult(errorOrResult) ||
    isTestFeatureUnavailableError(errorOrResult)
  );
}

export function notifyTestFeatureUnavailable(featureName = "This feature") {
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(
    new CustomEvent(TEST_FEATURE_UNAVAILABLE_EVENT, {
      detail: { featureName },
    }),
  );
}

/**
 * Blocks external AI / match-score calls on test. Shows the global modal and
 * returns a result object, or null when the call may proceed.
 */
export function getTestUnavailableResponse(featureName = "This feature") {
  if (!isTestEnvironment()) {
    return null;
  }
  notifyTestFeatureUnavailable(featureName);
  return {
    success: false,
    testUnavailable: true,
    error: `${featureName} is not available on the test environment.`,
  };
}

export function assertExternalAiAvailable(featureName = "This feature") {
  if (!isTestEnvironment()) {
    return;
  }
  notifyTestFeatureUnavailable(featureName);
  throw createTestFeatureUnavailableError(featureName);
}
